import { faExclamationTriangle, faTrashAlt } from '@fortawesome/free-solid-svg-icons'; // Replaced Ant Design icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
// Removed Ant Design imports
// Removed Paragraph import
import { API } from 'aws-amplify';
import moment from 'moment';
import { useEffect, useState } from 'react'; // Added useEffect
import { useNavigate } from 'react-router-dom';
import { ReactComponent as Logo } from '../../../images/svg/cb_log_2024_orange_white.svg';
import { useLocalization } from '../../../localization'; // Added import
import { useGlobalState } from '../../../store';
import { signoutUser } from '../../../utils/auth';
import { accountIconMap } from '../../../utils/customIcons';
import { CBButton, CBCard, CBModal } from '../../core/components/cbd'; // Assuming CBCard, CBModal are in cbd
import BillingForm from '../components/BillingForm';
import { CBSpin } from '../components/CBSpin'; // Added CBSpin import
// import { CBAlert } from '../components/CBAlert'; // CBAlert not used, using alert()

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);

const TrialBlockPage = () => {
  const { t } = useLocalization(); // Added hook call
  const navigate = useNavigate();
  const [user] = useGlobalState('user');
  const [license] = useGlobalState('license');
  const [schedules] = useGlobalState('schedules');
  const [syncs] = useGlobalState('syncs');
  const [accounts] = useGlobalState('accounts');
  const [loadRedirect, setLoadRedirect] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  // State for modals
  const [showCancelTrialModal, setShowCancelTrialModal] = useState(false);
  const [showCancelSubModal, setShowCancelSubModal] = useState(false);
  const [showRemoveAccountModal, setShowRemoveAccountModal] = useState(false);
  const [accountToRemove, setAccountToRemove] = useState(null);


  // Simulate data loading check
  useEffect(() => {
    if (user && license && schedules && syncs && accounts) {
      setLoadingData(false);
    }
    // Add dependencies if these states might load asynchronously after initial render
  }, [user, license, schedules, syncs, accounts]);


  async function getPortalUrl() {
    setLoadRedirect(true);
    try {
      const resp = await API.get('billing', '/billing?session=true', {});
      setLoadRedirect(false);
      window.location.href = resp.message;
    } catch (ex) {
      setLoadRedirect(false);
      // Replaced message.error with alert()
      alert(t('settings.portal_access_error'));
    }
  }

  const transfer = (id) => { // This is the account removal function
    // Consider adding loading state here specific to removal
    API.del('account', '/account?transfer=true&id=' + id, { // 'transfer=true' seems odd for deletion? Verify API endpoint.
      body: {},
    })
      .then(async () => {
        if(window != null) {
          window.location.reload(); // Consider state update instead
        }
      })
      .catch((error) => {
        // Replaced message.error with alert()
        alert(t('common.api_error', { message: error?.response?.data?.message || 'Unknown error' }));
      });
  };

  // Handler for modal confirmation (used by multiple modals)
  const handleCancelAccountOrSub = async () => {
    setShowCancelTrialModal(false); // Close both just in case
    setShowCancelSubModal(false);
    setLoadRedirect(true); // Use existing loading state
    try {
        const resp = await API.post('billing', '/billing', {
          body: {
            cancel: true,
          },
        });
        setLoadRedirect(false);
        if (resp.license.trial) {
            alert(t('settings.cancel_trial_success_message')); // Use alert
        } else {
            const cancelDate = moment(resp.license.cancelEnd * 1000).format('LL');
            alert(t('settings.cancel_sub_success_message', { date: cancelDate })); // Use alert
        }
        setTimeout(() => signoutUser(navigate), 1500);
    } catch (error) {
        setLoadRedirect(false);
        alert(t('common.api_error', { message: error?.response?.data?.message || 'Cancellation failed' }));
    }
  }

   // Handler for remove account confirmation
   const handleRemoveAccountConfirm = () => {
    if (!accountToRemove) return;
    setShowRemoveAccountModal(false);
    transfer(accountToRemove.calendarAccountId); // Call the transfer/delete function
    setAccountToRemove(null);
  };


  // Show loading spinner until all necessary data is available
  if (loadingData) {
      // Replaced Spin with CBSpin
      return (
          <div className="flex justify-center items-center h-screen">
              <CBSpin
                spinning={true}
                size="large"
                
              />
          </div>
      );
  }

  // Ensure license exists before trying to access its properties
  if (!license) {
      console.error("License data not available in TrialBlockPage");
      return <div>{t('common.loading_error')}</div>; // Or redirect
  }


  return (
    <div> {/* Main wrapper div */}
      <div style={{ marginTop: 40, marginBottom: 40 }} className="p-4 lg:p-0">
        <a href="https://calendarbridge.com">
          <Logo width="200px" height="50px"></Logo>
        </a>

        {/* Replaced Row/Col with Flexbox/Grid */}
        <div className="flex flex-col lg:flex-row mt-5 gap-5"> {/* Replaced Row */}
          {/* Left Column */}
          <div className="w-full lg:w-1/4"> {/* Replaced Col */}
            {license.state === 'valid' && (
              <h3 className="text-xl font-semibold mb-2">{t('trial_block.expired_title')}</h3>
            )}
            {license.state === 'suspended' && (
              <h3 className="text-xl font-semibold mb-2">{t('trial_block.paused_title')}</h3>
            )}
            {/* Replaced Paragraph with p */}
            <p className="text-sm text-gray-700 space-y-2"> {/* Added spacing */}
              <span className="block"> {/* Use span/div for block elements */}
                <b>{t('trial_block.syncs_disabled')}</b>
                {license.state === 'valid' && (
                  <>
                    {' '}{t('trial_block.expired_message')}
                  </>
                )}
              </span>

              <span className="block mt-4"> {/* Added margin */}
                <b>{t('settings.email_label')}</b> {license.email}
              </span>
              <span className="block">
                <b>{t('settings.organization_label')}</b>{' '}
                {license.orgId === 'single' ? t('settings.org_individual') : license.orgId}
              </span>
              <span className="block">
                <b>{t('settings.account_status_label')}</b> {license.active ? t('settings.status_active') : t('settings.status_inactive')}
              </span>

              {accounts && (
                <div className="mt-4 space-y-1"> {/* Added spacing */}
                  <b>{t('trial_block.account_summary')}</b>
                  <div>
                    <b>{t('trial_block.connected_accounts_label')}</b> {accounts.length}
                    {accounts.length === 0 && (
                      <div className="text-red-500 text-lg">
                        {t('trial_block.no_accounts_warning')}
                      </div>
                    )}
                  </div>
                  <div>
                    <b>{t('trial_block.syncs_label')}</b> {syncs.length}
                  </div>
                  <div>
                    <b>{t('trial_block.schedulers_label')}</b> {schedules.length}
                  </div>
                </div>
              )}
              {/* Cancel Trial Button */}
              {license.trial && (
                <div className="mt-4">
                  <CBButton
                    type="ternary"
                    loading={loadRedirect}
                    onClick={(e) => {
                      // Set state to show cancel trial modal
                      setShowCancelTrialModal(true);
                    }}
                  >
                    {t('settings.cancel_trial_button')}
                  </CBButton>
                </div>
              )}
              {/* Billing Portal & Cancel Subscription */}
              {!license.trial &&
                license.stripeUserId !== 'site' &&
                license.stripeUserId !== 'trial' && (
                  <div className="flex flex-col gap-4 font-sarabun mt-4">
                    {t('trial_block.payment_details_prompt')}
                    <CBButton
                      onClick={(e) => getPortalUrl()}
                      type="ternary"
                      loading={loadRedirect}
                    >
                      {t('settings.view_invoices_button')}
                    </CBButton>
                    <div className="mt-4">
                      <CBButton
                        type="ternary"
                        loading={loadRedirect}
                        onClick={(e) => {
                           // Set state to show cancel subscription modal
                           setShowCancelSubModal(true);
                        }}
                      >
                        {t('trial_block.cancel_account_button')}
                      </CBButton>
                    </div>
                  </div>
                )}
              <div className="mt-16"> {/* Replaced style with Tailwind */}
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    signoutUser(navigate);
                  }}
                  className="text-blue-600 hover:underline" // Added styling
                >
                  {t('home.logout')}
                </a>
              </div>
            </p>
          </div>
          {/* Right Column */}
          <div className="w-full lg:w-3/4"> {/* Replaced Col */}
            <CBCard> {/* Corrected closing tag */}
              <h3 className="text-xl font-semibold mb-4">{t('trial_block.billing_info_title')}</h3> {/* Added styling */}
              <Elements stripe={stripePromise}>
                <BillingForm
                  isLoading={false} // Pass loading state if needed
                  successMessage={t('trial_block.charge_success_message')}
                  reloadAfterSuccess={true}
                  showCard
                  showPlan={
                    license.active === false && license.state === 'suspended'
                      ? false
                      : true
                  }
                />
              </Elements>
            </CBCard>
            {/* Connected Accounts List */}
            {accounts.length > 0 &&
              <div className="mt-8"> {/* Added margin */}
                <div className="text-lg ml-4 mb-2">{t('trial_block.connected_accounts_header')}</div> {/* Localized */}
                {accounts.map((account) => {
                    return (
                      <div key={account.calendarAccountId} className="text-lg p-4 m-2 border rounded flex flex-row justify-between items-center "> {/* Added key and rounded */}
                        <div className="flex flex-row items-center gap-4">
                          <FontAwesomeIcon
                              icon={accountIconMap[account.calendarAccountType]?.icon || 'calendar'} // Added fallback icon
                              size="2x"
                              color={accountIconMap[account.calendarAccountType]?.color || '#888'} // Added fallback color
                          />
                          <div className="break-all">{account.email}</div> {/* Added break-all */}
                        </div>
                        {/* Replaced Modal.confirm with state setter */}
                        <CBButton type="ternary" className="text-red-600 hover:text-red-800" onClick={() => { // Added styling
                          setAccountToRemove(account);
                          setShowRemoveAccountModal(true);
                        }}>
                          {t('common.remove_button')} {/* Localized */}
                        </CBButton>
                      </div>
                    );
                  })}
              </div>}
          </div> {/* Corrected closing tag for Col replacement */}
        </div> {/* Corrected closing tag for Row replacement */}
      </div>

       {/* Modal Definitions */}
       <CBModal
          title={t('settings.cancel_trial_confirm_title')}
          visible={showCancelTrialModal}
          onPositiveClick={handleCancelAccountOrSub}
          onCancelClick={() => setShowCancelTrialModal(false)}
          positiveLabel={t('trial_block.end_trial_button')}
          negativeLabel={t('common.nevermind_button')}
          positiveButtonProps={{ className: 'bg-red-600 hover:bg-red-700' }}
        >
           <div className="flex items-center">
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-500 text-xl mr-3" />
              <p>{t('settings.cancel_trial_confirm_message')}</p> {/* Add confirmation message */}
           </div>
       </CBModal>

       <CBModal
          title={t('settings.cancel_sub_confirm_title')}
          visible={showCancelSubModal}
          onPositiveClick={handleCancelAccountOrSub}
          onCancelClick={() => setShowCancelSubModal(false)}
          positiveLabel={t('settings.cancel_sub_button')}
          negativeLabel={t('common.nevermind_button')}
          positiveButtonProps={{ className: 'bg-red-600 hover:bg-red-700' }}
        >
           <div className="flex items-center">
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-500 text-xl mr-3" />
              <p>{t('settings.cancel_sub_confirm_message')}</p> {/* Add confirmation message */}
           </div>
       </CBModal>

       <CBModal
          title={t('trial_block.remove_account_confirm_title_short')}
          visible={showRemoveAccountModal}
          onPositiveClick={handleRemoveAccountConfirm}
          onCancelClick={() => { setAccountToRemove(null); setShowRemoveAccountModal(false); }}
          positiveLabel={t('common.delete_button')}
          negativeLabel={t('common.cancel_button')}
          positiveButtonProps={{ className: 'bg-red-600 hover:bg-red-700' }}
        >
           <div className="flex items-center">
              <FontAwesomeIcon icon={faTrashAlt} className="text-red-600 text-xl mr-3" />
              <p>{t('trial_block.remove_account_confirm_message', { email: accountToRemove?.email })}</p> {/* Dynamic message */}
           </div>
       </CBModal>

    </div>
  );
};

export default TrialBlockPage;
